import React, { useState } from 'react';
import { runAlerts } from '../../api/alerts';
import { runSecuritiesEodIngest } from '../../api/securitiesEodRates';
import { refreshAlerts } from '../../hooks/useAlerts';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './SysadminToolsPage.css';
import { LopDateInput } from '../../components/LopDateInput';

// "מסך עזר" — SysAdmin-only helper screen (resourceCode `sysadminTools`).
// It hosts manual admin actions: running the per-tenant sp_RunAlerts, and
// triggering the TASE securities end-of-day rates ingest. The page is gated by
// /api/pages on the server (hidden from non-admins entirely) and each run
// endpoint is double-gated by the [RequirePermission("sysadminTools","Edit")]
// attribute on the backend.

type RunState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'ok'; count: number }
  | { kind: 'err'; message: string };

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function SysadminToolsPage() {
  const [run, setRun] = useState<RunState>({ kind: 'idle' });
  const [taseRun, setTaseRun] = useState<RunState>({ kind: 'idle' });
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());

  async function handleRun() {
    setRun({ kind: 'running' });
    try {
      const res = await runAlerts();
      // Refresh the shared alerts cache → TopBar heart + dashboard both reflect.
      await refreshAlerts().catch(() => undefined);
      setRun({ kind: 'ok', count: res.alertCount });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setRun({ kind: 'err', message });
    }
  }

  async function handleTaseRun() {
    setTaseRun({ kind: 'running' });
    try {
      const res = await runSecuritiesEodIngest(fromDate, toDate);
      setTaseRun({ kind: 'ok', count: res.rowsUpserted });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setTaseRun({ kind: 'err', message });
    }
  }

  return (
    <div className="sysadmin-tools">
      <h1 className="sysadmin-tools__title">{t('sysadminTools.pageTitle')}</h1>
      <p className="sysadmin-tools__subtitle">{t('sysadminTools.subtitle')}</p>

      <section className="sysadmin-tools__card">
        <h2 className="sysadmin-tools__card-title">{t('sysadminTools.runAlerts.title')}</h2>
        <p className="sysadmin-tools__card-desc">{t('sysadminTools.runAlerts.description')}</p>

        <LopButton
          variant="primary"
          className="sysadmin-tools__run-button"
          onClick={handleRun}
          loading={run.kind === 'running'}
        >
          {t('sysadminTools.runAlerts.button')}
        </LopButton>

        {run.kind === 'ok' && (
          <div className="sysadmin-tools__result sysadmin-tools__result--ok">
            {t('sysadminTools.runAlerts.success', { count: String(run.count) })}
          </div>
        )}
        {run.kind === 'err' && (
          <div className="sysadmin-tools__result sysadmin-tools__result--err">
            {run.message}
          </div>
        )}
      </section>

      <section className="sysadmin-tools__card">
        <h2 className="sysadmin-tools__card-title">{t('sysadminTools.runTaseRates.title')}</h2>
        <p className="sysadmin-tools__card-desc">{t('sysadminTools.runTaseRates.description')}</p>

        <div className="sysadmin-tools__date-row">
          <label className="sysadmin-tools__date-field">
            <span>{t('sysadminTools.runTaseRates.fromLabel')}</span>
            <LopDateInput value={fromDate} onChange={setFromDate} />
          </label>
          <label className="sysadmin-tools__date-field">
            <span>{t('sysadminTools.runTaseRates.toLabel')}</span>
            <LopDateInput value={toDate} onChange={setToDate} />
          </label>
        </div>

        <LopButton
          variant="primary"
          className="sysadmin-tools__run-button"
          onClick={handleTaseRun}
          loading={taseRun.kind === 'running'}
        >
          {t('sysadminTools.runTaseRates.button')}
        </LopButton>

        {taseRun.kind === 'ok' && (
          <div className="sysadmin-tools__result sysadmin-tools__result--ok">
            {t('sysadminTools.runTaseRates.success', { count: String(taseRun.count) })}
          </div>
        )}
        {taseRun.kind === 'err' && (
          <div className="sysadmin-tools__result sysadmin-tools__result--err">
            {taseRun.message}
          </div>
        )}
      </section>
    </div>
  );
}
